import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
function CountUp({
  end,
  suffix = '',
  duration = 2
}: {
  end: number;
  suffix?: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
        // easeOutQuart
        const easeProgress = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeProgress * end));
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    }
  }, [isInView, end, duration]);
  return <span ref={ref} data-id="element-204">
      {count}
      {suffix}
    </span>;
}
const requirements = [{
  num: '01',
  title: 'Staff Fluency',
  desc: 'Your existing staff must know exactly how to use AI tools effectively in their daily work.',
  graphic: <div className="w-full h-32 bg-neutral-50 rounded-lg border border-neutral-100 flex items-center justify-center relative overflow-hidden" data-id="element-205">
        <div className="absolute inset-0 bg-[radial-gradient(#e5e5e5_1px,transparent_1px)] [background-size:16px_16px] opacity-50" data-id="element-206" />
        <div className="relative flex gap-2" data-id="element-207">
          {[1, 2, 3].map(i => <motion.div key={i} className="w-8 h-8 rounded-full bg-foreground flex items-center justify-center" initial={{
        scale: 0
      }} whileInView={{
        scale: 1
      }} transition={{
        delay: i * 0.1,
        type: 'spring'
      }} viewport={{
        once: true
      }} data-id="element-208">
              <div className="w-2 h-2 bg-accent rounded-full" data-id="element-209" />
            </motion.div>)}
        </div>
      </div>
}, {
  num: '02',
  title: 'Process Integration',
  desc: 'AI must be woven into your existing business processes, not treated as a separate tool.',
  graphic: <div className="w-full h-32 bg-neutral-50 rounded-lg border border-neutral-100 flex items-center justify-center relative overflow-hidden" data-id="element-210">
        <div className="relative flex items-center w-full px-8" data-id="element-211">
          <div className="h-1 flex-1 bg-neutral-200 rounded-full overflow-hidden" data-id="element-212">
            <motion.div className="h-full bg-accent" initial={{
          width: 0
        }} whileInView={{
          width: '100%'
        }} transition={{
          duration: 1,
          delay: 0.2
        }} viewport={{
          once: true
        }} data-id="element-213" />
          </div>
          <motion.div className="w-10 h-10 rounded-lg bg-white border-2 border-accent absolute left-1/2 -translate-x-1/2 flex items-center justify-center shadow-sm" initial={{
        rotate: -45,
        scale: 0
      }} whileInView={{
        rotate: 0,
        scale: 1
      }} transition={{
        type: 'spring',
        delay: 0.5
      }} viewport={{
        once: true
      }} data-id="element-214">
            <div className="w-4 h-4 bg-accent/20 rounded-sm" data-id="element-215" />
          </motion.div>
        </div>
      </div>
}, {
  num: '03',
  title: 'Measured Gains',
  desc: 'You must track and capture the efficiency gains to actually realize the ROI.',
  graphic: <div className="w-full h-32 bg-neutral-50 rounded-lg border border-neutral-100 flex items-end justify-center gap-3 p-6 relative overflow-hidden" data-id="element-216">
        {[40, 70, 100].map((h, i) => <motion.div key={i} className="w-8 bg-foreground rounded-t-sm" initial={{
      height: 0
    }} whileInView={{
      height: `${h}%`
    }} transition={{
      duration: 0.5,
      delay: i * 0.15
    }} viewport={{
      once: true
    }} data-id="element-217" />)}
      </div>
}];
export function OutcomeSection() {
  return <section className="py-24 md:py-32 bg-white" data-id="element-218">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-219">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-220">
          <div data-id="element-221">
            <h2 className="text-4xl md:text-6xl font-serif mb-6 leading-tight" data-id="element-222">
              The math is simple.
              <br data-id="element-223" />
              The execution is hard.
            </h2>
            <p className="text-lg text-neutral-600 max-w-md" data-id="element-224">
              Expecting your company to operate more efficiently just because AI
              exists is a mistake. It requires deliberate implementation.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-8 lg:justify-end" data-id="element-225">
            <div className="bg-white p-8 rounded-2xl border border-neutral-200 shadow-sm flex-1" data-id="element-226">
              <div className="text-5xl md:text-7xl font-serif text-accent mb-2" data-id="element-227">
                <CountUp end={30} suffix="%" data-id="element-228" />
              </div>
              <div className="text-sm font-medium text-neutral-500 uppercase tracking-wider" data-id="element-229">
                More Efficient
              </div>
            </div>
            <div className="bg-foreground text-white p-8 rounded-2xl shadow-xl flex-1" data-id="element-230">
              <div className="text-5xl md:text-7xl font-serif mb-2" data-id="element-231">
                <CountUp end={20} suffix="%" data-id="element-232" />
              </div>
              <div className="text-sm font-medium text-neutral-400 uppercase tracking-wider" data-id="element-233">
                Less Staff
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-234">
          {requirements.map((req, i) => <motion.div key={i} className="bg-white p-8 rounded-2xl border border-neutral-200" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} viewport={{
          once: true
        }} data-id="element-235">
              <div className="text-xs font-bold text-accent mb-6" data-id="element-236">
                {req.num}
              </div>
              {req.graphic}
              <h3 className="text-xl font-serif mt-8 mb-3" data-id="element-237">{req.title}</h3>
              <p className="text-neutral-600 text-sm leading-relaxed" data-id="element-238">
                {req.desc}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}